import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';

import {
  Grid,
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@material-ui/core';

import {
  MultiSelect,
  Select,
  OutlinedTextField,
  OutlinedTextArea,
  Editor
} from '../common/fields';
import FilePicker from '../common/filePicker';
import FilePickerMulti from '../common/filePickerMulti';
import Alert from '../common/alert';
import Loading from '../common/loading';

import ItemLines from './ItemLines';
import Markets from './Markets';
import ActionBar from './ActionBar';
import MainImage from './MainImage';
import BulletList from './BulletList';
import MasterCountryTooltip from './MasterCountryTooltip';
import TyreAwardsList from '../tyreAwards/tyreAwardsList';
import UserReviewsList from '../userReviews/UserReviewsList';

import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectGlobals } from '../app/appSlice';
import {
  selectMasterCountry,
  selectCurrentCountry,
  selectCountriesWithNames
} from '../countries/countriesSlice';
import {
  selectSaveStatus,
  clearSaveStatus,
  selectMaster,
  saveItemFamilyDraft,
  saveItemFamilyPublished,
  publishItemFamily,
  selectEqualStatus,
  selectAwards,
  selectReviews
} from './itemFamilySlice';


const ItemFamilyPagePC1 = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { itemOwnerId } = useParams();

  const { status, itemFamily, canEdit: userAllowedToEdit, canEditRoleBasedFields } = props;

  console.log('canEditRoleBasedFields', canEditRoleBasedFields);

  const { saveStatus, saveError } = useSelector(selectSaveStatus);
  const masterItemFamily = useSelector(selectMaster);
  const { vehicleTypes, seasons, brands, attributes, feeds } = useSelector(selectGlobals);
  const masterCountry = useSelector(selectMasterCountry);
  const currentCountry = useSelector(selectCurrentCountry);
  const allCountries = useSelector(selectCountriesWithNames);
  const equalStatus = useSelector(selectEqualStatus);
  const { awards, awardsStatus, awardsError } = useSelector(selectAwards);
  const { reviews, reviewsStatus, reviewsError } = useSelector(selectReviews);

  const [ itemFamilyData, setItemFamilyData ] = useState(itemFamily || {});
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  const handleChange = (field, value) => {
    setItemFamilyData({
      ...itemFamilyData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    let value = event.target.value;

    if(event.target.type === 'checkbox') {
      value = event.target.checked ? '1' : '0';
    }

    handleChange(event.target.name, value);
  }

  const saveDraft = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(saveItemFamilyDraft({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData, canEditRoleBasedFields}));
    }
  };

  const saveDraftAndPublish = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(publishItemFamily({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData, canEditRoleBasedFields}));
    }
  }

  const savePublished = () => {
    // Save to server
    if(status === 'published') {
      dispatch(saveItemFamilyPublished({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData, canEditRoleBasedFields}));
    }
  };

  const cancelChanges = () => {
    setItemFamilyData(itemFamily);
    setHasUnsavedChanges(false);
  };

  const renderTooltip = (content, type) => {
    if(!content || content.length === 0) {
      return null;
    }

    if(currentCountry === masterCountry) {
      return null;
    }

    return <MasterCountryTooltip content={content} type={type} masterCountry={masterCountry} />;
  }

  useEffect(() => {
    // Parent props change - basically draft vs publish change
    setItemFamilyData(itemFamily);
    setHasUnsavedChanges(false);
  }, [itemFamily]);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  if(!itemFamily.code) {
    const message = status === 'published' ? 'notPublished' : 'notFound';
    return <Alert severity="info">{t(`itemFamily:${message}`)}</Alert>
  } else {

    return (
      <>
        <Grid container spacing={7}>
          <Grid item xs={12} md={8}>
            {status === 'draft' ? (equalStatus ? null : <Box mb={2}><Alert severity="info">{t('itemFamily:containsUnpunblishedChanges')}</Alert></Box>) : null}

            <Box mb={2}>
              <Typography variant="subtitle1">{t('itemFamily:itemFamily')}</Typography>
              <Typography variant="h4" component="h1">{itemFamilyData.name}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="overline" display="block">{t('itemFamily:lastChanged')}</Typography>
              <Typography variant="caption">{itemFamilyData.updated}</Typography>
            </Box>

            <Box mb={4}>
              <Button variant="contained" color="primary" disableElevation component={RouterLink} to={`/pih/owner/${itemOwnerId}/items?itemFamilyCode=${encodeURIComponent(itemFamily.code)}`} target="_blank">{t('itemFamily:showItems')}</Button>
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:itemLines')}</Typography></Box>
              <ItemLines items={itemFamilyData.itemLines} handleChange={newVal => handleChange('itemLines', newVal)} vehicleTypes={vehicleTypes} seasons={seasons} disabled={!userAllowedToEdit} />
            </Box>

            <Box my={3}>
              <Select
                label={t('itemFamily:brand')}
                name="brand"
                value={itemFamilyData.brand}
                options={brands.map(item => ({label: item.name, value: item.code}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Markets allCountries={allCountries} selectedCountries={itemFamilyData.markets} handleChange={newVal => handleChange('markets', newVal)} disabled={!userAllowedToEdit} />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:feeds')}</Typography></Box>
              <MultiSelect
                label={t('itemFamily:chooseFeeds')}
                name="feeds"
                value={itemFamilyData.feeds}
                options={feeds.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:content')}</Typography></Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:marketingName')}
                  name="marketingName"
                  value={itemFamilyData.marketingName}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingName)}
                  maxLength={50}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:marketingSlogan')}
                  name="marketingSlogan"
                  value={itemFamilyData.marketingSlogan}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingSlogan)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>

                <OutlinedTextField
                  label={t('itemFamily:marketingTextTitle')}
                  name="marketingTextTitle"
                  value={itemFamilyData.marketingTextTitle}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingTextTitle)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:vehicleTypeDescription')}
                  name="vehicleTypeDescription"
                  value={itemFamilyData.vehicleTypeDescription}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.vehicleTypeDescription)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('itemFamily:marketingTextShort')}
                  name="marketingTextShort"
                  value={itemFamilyData.marketingTextShort}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingTextShort)}
                  maxLength={260}
                />
              </Box>

              <Box my={3}>
                <Editor
                  label={t('itemFamily:marketingText')}
                  name="marketingText"
                  value={itemFamilyData.marketingText}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingText, 'html')}
                  maxLength={500}
                />
              </Box>

              <Box my={3}>
                <Typography variant="subtitle1">{t('itemFamily:marketingBullets')}</Typography>
                <BulletList
                  tooltip={renderTooltip(masterItemFamily.marketingBullets, 'htmlList')}
                  data={itemFamilyData.marketingBullets || []}
                  userAllowedToEdit={userAllowedToEdit}
                  onEdit={(data) => handleChange('marketingBullets', data)}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:metaTitle')}
                  name="metaTitle"
                  value={itemFamilyData.metaTitle}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.metaTitle)}
                  maxLength={100}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('itemFamily:metaDescription')}
                  name="metaDescription"
                  value={itemFamilyData.metaDescription}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.metaDescription)}
                  maxLength={160}
                />
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:attributes')}</Typography></Box>

              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:chooseAttributes')}
                  name="attributes"
                  value={itemFamilyData.attributes}
                  options={attributes.attributes.map(item => ({label: item.global_name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:chooseGuaranteeAttributes')}
                  name="attributesGuarantee"
                  value={itemFamilyData.attributesGuarantee}
                  options={attributes.attributesGuarantee.map(item => ({label: item.global_name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                {canEditRoleBasedFields?.comingSoon === true ?
                  <>
                    <FormControlLabel
                      control={<Checkbox checked={itemFamilyData.comingSoon === '1' ? true : false} name="comingSoon" color="primary" onChange={handleFieldChange} value="1" />}
                      label={t('itemFamily:comingSoon')}
                    />
                    <FormHelperText>{t('itemFamily:comingSoonHelpText')}</FormHelperText>
                  </>
                : <p>{t('itemFamily:comingSoonAttributePresent')}</p>}
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:images')}</Typography></Box>

              {['image25degreeFull', 'image25degreeHalf', 'imageFrontFull', 'imageFrontHalf', 'imageSideFull', 'imageCut3d'].map(field =>
                <FilePicker key={field} fieldName={t(`itemFamily:${field}`)} fieldValue={itemFamilyData[field]} setFieldValue={(file) => handleChange(field, file)} disabled={!userAllowedToEdit} />
              )}

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:moodImages')}</Typography></Box>

              <FilePickerMulti
                fieldName={t('itemFamily:moodImages')}
                fieldValue={itemFamilyData.moodImages}
                setFieldValue={(files) => handleChange('moodImages', files)}
                btnLabel={t('itemFamily:addImages')}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:videos')}</Typography></Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:youtubeVideoUrl')}
                  name="youtubeVideoUrl"
                  value={itemFamilyData.youtubeVideoUrl}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.youtubeVideoUrl)}
                />
              </Box>

              <FilePicker
                fieldName={t('itemFamily:marketingVideo')}
                fieldValue={itemFamilyData.marketingVideo}
                setFieldValue={(file) => handleChange('marketingVideo', file)}
                disabled={!userAllowedToEdit}
              />

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:userReviews')}</Typography></Box>
              {reviewsStatus === 'loading' ? <Loading /> : null}
              {reviewsStatus === 'failed' ? <Alert severity="error">{t(`errors:${reviewsError.message}`)}</Alert> : null}
              {reviewsStatus === 'succeeded' ? <UserReviewsList items={reviews} noResultsMessage={t('itemFamily:noReviews')} rows={25} /> : null}
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:tyreAwards')}</Typography></Box>
              {awardsStatus === 'loading' ? <Loading /> : null}
              {awardsStatus === 'failed' ? <Alert severity="error">{t(`errors:${awardsError.message}`)}</Alert> : null}
              {awardsStatus === 'succeeded' ? <TyreAwardsList items={awards} noResultsMessage={t('itemFamily:noAwards')} rows={25} /> : null}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <MainImage src={itemFamilyData.imageMain?.medium} />

            <FilePicker fieldName={t('itemFamily:imageMain')} fieldValue={itemFamilyData.imageMain} setFieldValue={(file) => handleChange('imageMain', file)} disabled={!userAllowedToEdit} />
          </Grid>

        </Grid>
        {hasUnsavedChanges
          ?
          <ActionBar>
            <Button variant="outlined" color="primary" onClick={cancelChanges}>{t('common:cancelChanges')}</Button>
            {
              status === 'draft' ?
                <>
                  <Button variant="contained" color="primary" onClick={saveDraft} disabled={saveStatus === 'loading'}>{t('common:saveChanges')}</Button>
                  <Button variant="contained" color="primary" onClick={saveDraftAndPublish} disabled={saveStatus === 'loading'}>{t('common:saveAndPublishChanges')}</Button>
                </>
                :
                <Button variant="contained" color="primary" onClick={savePublished} disabled={saveStatus === 'loading'}>{t('common:publishChanges')}</Button>
            }

          </ActionBar>
          :
          null
        }
      </>
    );
  }
};

ItemFamilyPagePC1.propTypes = {
  status: PropTypes.oneOf(['draft', 'published']).isRequired,
  itemFamily: PropTypes.object
};

export default ItemFamilyPagePC1;
